/* eslint-disable no-undefined */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import TrimbleMaps from '@trimblemaps/trimblemaps-js';
import { combineLatest, fromEvent, Observable } from 'rxjs';
import { filter, skipWhile, switchMap, withLatestFrom } from 'rxjs/operators';
import { MapService } from 'src/app/services/map.service';
import { RouteService } from 'src/app/services/route.service';
import { MileageReportResponse } from 'src/app/_models/pcmws/reports/mileageReportResponse';
import { StopLocation } from 'src/app/_models/state/stopState';
import { MapRoute } from 'src/app/_models/view/mapRoute';
import { AlertService } from 'src/app/auth/services/alert.service';
import { SubscriptionManager } from 'src/app/_helpers/subscription-manager';
import { ApplicationSettingsService } from 'src/app/services/application-settings.service';
import { RoutingProfile } from 'src/app/_models/daws/trip';
import { cloneDeep } from 'lodash-es';
import { ConversionService } from 'src/app/services/conversion.service';
import { RouteState } from 'src/app/_models/state/routeState';
import { AuthenticationService } from 'src/app/auth/services/authentication.service';
import { RoutingProfileModel } from 'src/app/_models/profilews/profilews';
@Component({
  selector: 'app-map-route',
  template: '',
  styles: [],
})
export class MapRouteComponent implements OnInit, OnDestroy {
  @Input() routeId: number;
  subscriptionManager = new SubscriptionManager();
  route$: Observable<MapRoute>;
  mapRoute: TrimbleMaps.Route;
  isVisible = true;
  // This is used to avoid double submits when changes are driven from the map like drag routing
  skipNextUpdate = false;
  routeMapState: MapRoute;
  historicalDataVersion: string;
  routeProfile: RoutingProfile;
  isEuRegion: any;
  isRouteLoading: boolean;
  historicalReportSubscribeCount = 0;
  selectedRoute: RouteState;
  showLoading = true;
  constructor(
    private alertService: AlertService,
    private mapService: MapService,
    private routeService: RouteService,
    private appSettingsService: ApplicationSettingsService,
    private conversionService: ConversionService,
    private authenticationService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.route$ = this.routeService.getMapRoute(this.routeId);
    this.subscriptionManager.manage = combineLatest([
      this.mapService.map$.pipe(skipWhile((map) => map === null)),
      this.route$,
      this.routeService.selectedRoute$,
      this.authenticationService.currentUser,
    ]).subscribe(([map, route, selectedRoute, userInfo]) => {
      if (
        route.historicalDataVersion !== 'current' &&
        route.historicalDataVersion !== 'PCM_EU'
      ) {
        this.historicalReportSubscribeCount =
          this.historicalReportSubscribeCount + 1;
      } else {
        this.historicalReportSubscribeCount = 0;
      }
      this.selectedRoute = selectedRoute;
      const previousRouteState = this.routeService.previousRouteState
        ?.filter((rs) => {
          return rs.id === route.id;
        })
        .shift();
      this.showLoading = previousRouteState
        ? previousRouteState.isVisibleOnMap === route.isVisibleOnMap
          ? true
          : false
        : true;
      if (previousRouteState) {
        this.skipNextUpdate =
          previousRouteState.historicalDataVersion !==
            route.historicalDataVersion &&
          route.historicalDataVersion !== 'current';
      }

      if (
        route?.options?.stops?.length >= 2 &&
        selectedRoute.id === route?.id &&
        this.showLoading &&
        this.historicalReportSubscribeCount === 0
      ) {
        this.routeService.setRouteLoadingState(true);
      }
      const updateRouteOption: MapRoute = this.getOptions(
        route,
        selectedRoute,
        userInfo
      );

      if (!this.mapRoute) {
        this.initializeMapRoute(updateRouteOption, map);
      } else {
        //check for updates and update MapRoute
        if (
          JSON.stringify(this.routeMapState) !==
          JSON.stringify(updateRouteOption)
        ) {
          this.updateMapRoute(updateRouteOption, map);
        } else {
          if (
            this.isRouteLoading &&
            this.historicalReportSubscribeCount === 0 &&
            selectedRoute.id === route?.id
          ) {
            this.routeService.setRouteLoadingState(false);
          } else {
            setTimeout(
              (ctx) => {
                if (ctx.historicalReportSubscribeCount > 0) {
                  ctx.routeService.setRouteLoadingState(false);
                }
              },
              2000,
              this
            );
          }
        }
      }

      this.mapRoute.on('report', (d) => {
        if (
          (d[0]?.RouteID === this.selectedRoute.id.toString() &&
            this.historicalReportSubscribeCount) === 0
        ) {
          this.routeService.setRouteLoadingState(false);
        }
      });
      this.mapRoute.on('error', (d) => {
        this.routeService.setRouteLoadingState(false);
      });
      //setting the updated route option to compare
      this.routeMapState = updateRouteOption;
    });
    this.subscriptionManager.manage = this.routeService.isLoading$.subscribe(
      (loading) => {
        this.isRouteLoading = loading;
      }
    );
    this.subscriptionManager.manage = this.routeService.frameRouteId$
      .pipe(
        filter((rid) => this.routeId === rid),
        switchMap(() => this.route$)
      )
      .subscribe((route) => {
        if (route.isVisibleOnMap) {
          this.mapRoute?.frame();
        }
      });

    this.subscriptionManager.manage = this.routeService.optimizeRoute$
      .pipe(
        filter((evt) => this.routeId === evt.routeId),
        withLatestFrom(
          this.mapService.map$.pipe(skipWhile((map) => map === null)),
          this.route$,
          this.routeService.selectedRoute$,
          this.authenticationService.currentUser
        )
      )
      .subscribe(([evt, map, route, selectedRoute, userInfo]) => {
        const routeMap = this.getOptions(route, selectedRoute, userInfo);
        const newRoute: MapRoute = {
          ...route,
          options: {
            ...routeMap?.options,
            routeOptimization: evt.optimization,
          },
        };
        this.updateMapRoute(newRoute, map);
      });
  }

  getOptions(route, selectedRoute, userInfo): MapRoute {
    this.isEuRegion = userInfo?.region?.toUpperCase() === 'EU';
    const profiles = JSON.parse(
      localStorage.getItem('routingProfileStore')
    ).profiles;
    const selectedProfile: RoutingProfileModel = profiles
      .filter((pf) => pf.name === route.options.profileName)
      .shift();

    //update routeoptions based on selected profile.
    route.options.vehicleType = this.routeService.getVehicleType(
      selectedProfile?.vehicleType
    );
    route.options.governorSpeedLimit =
      selectedProfile?.vehicleOptions?.governorSpeedLimit;
    route.options.hazMatType = selectedProfile?.vehicleOptions?.hazmatType
      ? selectedProfile?.vehicleOptions?.hazmatType
      : 0;
    route.options.congestionZone = this.isEuRegion
      ? selectedProfile?.routingOptions?.restrictionZones
        ? selectedProfile?.routingOptions.restrictionZones.allow.find(
            (z) => z === 'congestionZones'
          )
          ? TrimbleMaps.Common.ZoneType.ALLOW
          : selectedProfile?.routingOptions.restrictionZones.avoid.find(
              (z) => z === 'congestionZones'
            )
          ? TrimbleMaps.Common.ZoneType.AVOID
          : TrimbleMaps.Common.ZoneType.WARN
        : TrimbleMaps.Common.ZoneType.ALLOW
      : undefined;
    if (selectedProfile?.routingOptions) {
      route.options.routeType = selectedProfile?.routingOptions?.routeType;
      route.options.tollRoads = selectedProfile?.routingOptions?.tollRoads;
      route.options.bordersOpen = selectedProfile?.routingOptions
        ?.useOpenBorders
        ? selectedProfile.routingOptions.useOpenBorders
        : false;
      route.options.ferryDiscourage = selectedProfile?.routingOptions?.ferries
        ? true
        : false;
      route.options.elevLimit = selectedProfile?.routingOptions?.elevationLimit;
    }

    const daRouteOptions = this.conversionService.convertToDARouteOptions(
      route.options.profileName,
      -1,
      route.options,
      selectedProfile,
      this.isEuRegion
        ? selectedRoute?.euProfile
          ? selectedRoute.euProfile
          : this.appSettingsService.getDefaultAppSettingsData(this.isEuRegion)
              ?.euProfile
        : null
    );
    this.routeProfile = daRouteOptions;
    const routeOptionLatest = this.conversionService.convertToRouteOptions(
      this.routeProfile,
      selectedProfile?.routingProfileId
    );
    //afSetIds get visible afSetIds from add-on and pass it here
    const updateRouteOption: MapRoute = {
      ...route,
      options: {
        ...route.options,
        useAvoidsAndFavors: routeOptionLatest.useAvoidsAndFavors,
        bordersOpen: routeOptionLatest.bordersOpen,
        overrideClass: routeOptionLatest.overrideClass,
        fuelConsumption: routeOptionLatest.fuelConsumption,
        fuelType: routeOptionLatest.fuelType,
        hazMatType: routeOptionLatest.hazMatType,
        highwayOnly: routeOptionLatest.highwayOnly,
        overrideRestrict: routeOptionLatest.overrideRestrict,
        refrigerated: routeOptionLatest.refrigerated,
        routeOptimization: routeOptionLatest.routeOptimization,
        routeType: routeOptionLatest.routeType,
        sideOfStreetAdherence: routeOptionLatest.sideOfStreetAdherence,
        tollRoads: routeOptionLatest.tollRoads,
        vehicleType: routeOptionLatest.vehicleType,
        governorSpeedLimit: routeOptionLatest?.governorSpeedLimit
          ? routeOptionLatest.governorSpeedLimit
          : undefined,
        elevLimit: routeOptionLatest?.elevLimit
          ? routeOptionLatest?.elevLimit
          : undefined,
        // tunnelCategories: routeOptionLatest?.tunnelCategories
        //   ? routeOptionLatest?.tunnelCategories
        //   : undefined,
        trkLCV: routeOptionLatest.trkLCV,
        trkUnits: routeOptionLatest.trkUnits,
        trkHeight: routeOptionLatest.trkHeight,
        trkLength: routeOptionLatest.trkLength,
        trkWidth: routeOptionLatest.trkWidth,
        trkWeight: routeOptionLatest.trkWeight,
        trkAxles: routeOptionLatest.trkAxles,
        trkMaxWeightPerAxleGroup: routeOptionLatest.trkMaxWeightPerAxleGroup,
        trkTireCount: routeOptionLatest.trkTireCount,
        //remove trl related options and engine from route options -updateRouteOption
        trlCount: this.isEuRegion ? routeOptionLatest?.trlCount : undefined,
        trlMaxAxles: this.isEuRegion
          ? routeOptionLatest?.trlMaxAxles
          : undefined,
        trlMaxHt: this.isEuRegion ? routeOptionLatest?.trlMaxHt : undefined,
        trlMaxWt: this.isEuRegion ? routeOptionLatest?.trlMaxWt : undefined,
        trlType: this.isEuRegion ? routeOptionLatest?.trlType : undefined,
        minPoll: this.isEuRegion ? routeOptionLatest?.minPoll : undefined,
        emiType: this.isEuRegion ? routeOptionLatest?.emiType : undefined,
        isHybrid: this.isEuRegion ? routeOptionLatest?.isHybrid : undefined,
      },
    };
    updateRouteOption.options.profileName = undefined;
    updateRouteOption.options.profileId = routeOptionLatest?.profileId;
    this.historicalDataVersion = route?.historicalDataVersion;
    return updateRouteOption;
  }

  ngOnDestroy(): void {
    this.mapRoute?.remove();
    this.subscriptionManager.unsubscribe();
    this.mapRoute = null;
  }

  initializeMapRoute(route: MapRoute, map: TrimbleMaps.Map) {
    this.isVisible = route.isVisibleOnMap;
    this.mapRoute = new TrimbleMaps.Route(route.options);

    if (route.isVisibleOnMap && map) {
      this.mapRoute.addTo(map);
      // needed for remove bug
      this.mapRoute.setDraggable(true);
    }
    const error$ = fromEvent(this.mapRoute, 'error').pipe(
      withLatestFrom(this.route$)
    );

    this.subscriptionManager.manage = combineLatest([
      error$,
      this.routeService.selectedRoute$,
    ]).subscribe(([e, selectedRoute]) => {
      // e[0].error.message;
      // e[0].error.name;
      // e[0].type;
      // e[0].target;
      let errorMessage: string = null;
      if (Array.isArray(e)) {
        // eslint-disable-next-line
        // @ts-ignore
        const msgs = JSON.parse(e[0]?.error?.message);
        if (Array.isArray(msgs)) {
          errorMessage = msgs[0]?.Description;
        }
      }
      if (errorMessage === null) {
        errorMessage =
          'There was an error getting the route or/and reports. Make sure the selected profile has proper values.';
      }
      const warnMsg =
        'Due to a technical issue, services are temporarily unavailable.  Please check back again for an updated status.';
      e.map((r: MapRoute) => {
        if (selectedRoute?.id === r?.id) {
          this.alertService.error(
            `${errorMessage}`,
            true,
            'app-modal-alert',
            true
          );
          this.alertService.warning(
            `${warnMsg}`,
            true,
            'app-modal-alert',
            true
          );
        }
        return r;
      });
    });
    const routeEvent$ = fromEvent(this.mapRoute, 'route').pipe(
      withLatestFrom(this.route$)
    );
    this.subscriptionManager.manage = combineLatest([
      routeEvent$,
      this.routeService.selectedRoute$,
    ]).subscribe(([e, selectedRoute]) => {
      e.map((r: MapRoute) => {
        if (selectedRoute?.id === r?.id) {
          this.alertService.clear();
        }
        return r;
      });
    });
    const report$ = fromEvent(this.mapRoute, 'report').pipe(
      withLatestFrom(this.route$)
    );
    this.subscriptionManager.manage = report$.subscribe(
      ([report, mapRoute]) => {
        // TODO switch to destructuring?
        const mileageReport: MileageReportResponse = report[0];
        const detailReport: MileageReportResponse = report[1];
        const drivingDirectionsReport: MileageReportResponse = report[2];
        const stateCountryReport: MileageReportResponse = report[3];
        const roadTypeReport: MileageReportResponse = report[4];
        const tollReport: MileageReportResponse = report[5];

        // if the stops in the mileage report are different from the route state
        // then a drag route  or optimization has occured and the route state needs to be updated.

        const mileageReportData = cloneDeep(mileageReport);
        if (this.shouldUpdateStops(mapRoute, mileageReportData)) {
          this.skipNextUpdate = true;
          let sortedStops = [];
          const routeStore = this.routeService.getStorage();
          const routeState = routeStore?.routeList?.find(
            (r) => Number(r?.detailReport?.RouteID) === mapRoute?.id
          );
          // Since the stops have changed, use the mileage report as the new source of stops.
          sortedStops = mileageReportData?.ReportLines.map((l) =>
            JSON.stringify(
              TrimbleMaps.LngLat.convert([
                Number.parseFloat(l.Stop.Coords.Lon),
                Number.parseFloat(l.Stop.Coords.Lat),
              ])
            )
          );
          const c = sortedStops.map((i) =>
            routeState?.stopsState?.find((j) => {
              return JSON.stringify(TrimbleMaps.LngLat.convert(j.lngLat)) === i;
            })
          );
          const locations = c?.map((stop, index) => {
            let loc;
            if (stop) {
              loc = {
                name: stop.location.name,
                address: stop.location.address,
                coords: stop.location.coords,
              } as StopLocation;
            } else {
              const routeStop = mileageReportData?.ReportLines[index]?.Stop;
              loc = {
                address: {
                  streetAddress: routeStop.Address.StreetAddress,
                  city: routeStop.Address.City,
                  stateAbbreviation: routeStop.Address.StateAbbreviation,
                  zip: routeStop.Address.Zip,
                  county: routeStop.Address.County,
                  countryAbbreviation: routeStop.Address.CountryAbbreviation,
                },
                coords: [
                  Number.parseFloat(routeStop.Coords.Lon),
                  Number.parseFloat(routeStop.Coords.Lat),
                ],
              } as StopLocation;
            }
            return loc;
          });
          if (locations) {
            this.routeService.updateRouteStops(mapRoute.id, locations);
          }
        }

        // TODO extract reporting from map (call services directly)
        this.routeService.updateRouteReports(this.routeId, {
          mileageReport,
          detailReport,
          drivingDirectionsReport,
          stateCountryReport,
          roadTypeReport,
          tollReport,
        });
        this.checkHistoricalDataVersion(route.options);
      }
    );
  }

  updateMapRoute(route: MapRoute, map: TrimbleMaps.Map) {
    // Some actions, like drag routing and optimization cause the route state to be updated.
    // Since the change originates from the map, the route is already up-to-date and doesn't need
    // to be run again.
    if (this.skipNextUpdate) {
      this.skipNextUpdate = false;
      if (this.historicalReportSubscribeCount === 0) {
        this.routeService.setRouteLoadingState(false);
      }
      return;
    }

    // visibility
    if (this.isVisible !== route.isVisibleOnMap) {
      this.isVisible = route.isVisibleOnMap;
      if (this.isVisible) {
        this.mapRoute = new TrimbleMaps.Route(route.options);
        this.mapRoute.addTo(map);
        this.mapRoute.setDraggable(true);
      } else {
        this.mapRoute.remove();
      }
      this.checkHistoricalDataVersion(route.options);
    } else {
      // Fails to update profile name. A bug in the SDK.
      // Todo-yz: after the SDK update, need to update this line.
      if (!route.options.profileName) {
        // eslint-disable-next-line
        // @ts-ignore
        delete this.mapRoute._options.profileName;
      }

      if (route.options.stops?.length < 2) {
        const mileageReport = null;
        const detailReport = null;
        const drivingDirectionsReport = null;
        const stateCountryReport = null;
        const roadTypeReport = null;
        const tollReport = null;

        this.routeService.updateRouteReports(this.routeId, {
          mileageReport,
          detailReport,
          drivingDirectionsReport,
          stateCountryReport,
          roadTypeReport,
          tollReport,
        });
      }

      this.mapRoute.update(route.options);
    }
  }

  private shouldUpdateStops(
    mapRoute: MapRoute,
    mileageReport: MileageReportResponse
  ) {
    if (mileageReport.ReportLines.length !== mapRoute.options.stops.length) {
      return true;
    }

    return !mileageReport.ReportLines.every((reportLine, idx) => {
      const optionsLngLat = TrimbleMaps.LngLat.convert(
        mapRoute.options.stops[idx].lngLat
      );

      return (
        Number.parseFloat(reportLine.Stop.Coords.Lon) === optionsLngLat.lng &&
        Number.parseFloat(reportLine.Stop.Coords.Lat) === optionsLngLat.lat
      );
    });
  }
  checkHistoricalDataVersion(routeOptions: TrimbleMaps.RouteOptions) {
    if (
      this.routeService.checkHistoricalDataVersion(this.historicalDataVersion)
    ) {
      if (
        this.routeMapState.id.toString() === routeOptions.routeId &&
        this.routeMapState.options.stops.length >= 1 &&
        this.showLoading
      ) {
        this.routeService.setRouteLoadingState(true);
      }
      this.routeProfile.dataVersion = routeOptions.dataVersion;
      const profiles = JSON.parse(
        localStorage.getItem('routingProfileStore')
      ).profiles;
      const selectedProfile = profiles
        .filter((pf) => pf.name === this.routeProfile?.name)
        .shift();
      const routeOption = this.conversionService.convertToRouteOptions(
        this.routeProfile,
        selectedProfile?.routingProfileId
      );
      const rOptions = { ...routeOption };
      //remove trl related options and engine from route options -updateRouteOption
      rOptions.trlCount = undefined;
      rOptions.trlMaxAxles = undefined;
      rOptions.trlMaxHt = undefined;
      rOptions.trlMaxWt = undefined;
      rOptions.trlType = undefined;
      rOptions.minPoll = undefined;
      rOptions.emiType = undefined;
      rOptions.isHybrid = undefined;
      this.routeService.generateHistoricalReport(
        rOptions,
        this.routeId,
        this.historicalDataVersion
      );
    }
  }
}
